import React, { useState, useRef, useEffect } from 'react';
import './Volunteer.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import apiService from '../services/api';

// --- Static Data ---

const VOLUNTEER_ROLES = [
  {
    icon: 'fa-chalkboard-teacher',
    title: 'Teacher',
    description: 'If you are a teacher or involved with education, you can spare some time for these children as well. Become a volunteer teacher, and lead the children to the light of knowledge.'
  },
  {
    icon: 'fa-user-md',
    title: 'Medical Professional',
    description: 'If you are a doctor or in the medical profession, we will be happy to welcome you to be a part of the different medical camps and in-house medical needs as a volunteer.'
  },
  {
    icon: 'fa-laptop-code',
    title: 'Computer Professional',
    description: 'If you are a computer professional, you can certainly be a volunteer in our team involved with computer training.'
  },
  {
    icon: 'fa-running',
    title: 'Sportsperson',
    description: 'If you are a sportsperson, we invite you to volunteer in our sports endeavors and help the children excel in sports.'
  },
  {
    icon: 'fa-flask',
    title: 'Scientist',
    description: 'If you are a scientist or involved with science, help the children know the marvels of science, in your free time.'
  },
  {
    icon: 'fa-music',
    title: 'Cultural Enthusiast',
    description: 'If you are into cultural activities, get involved in sharing your passion with the children through music, dance, art, and drama.'
  },
  {
    icon: 'fa-building',
    title: 'Corporate Executive',
    description: 'If you are a corporate executive, help us in fund-raising as a volunteer. You can help us acquire good sponsors for our programs.'
  },
  {
    icon: 'fa-share-alt',
    title: 'Social Media Expert',
    description: 'If you are into social media, use your platform to spread the word about us. Every bit of awareness and publicity would push us a step ahead.'
  },
  {
    icon: 'fa-hands-helping',
    title: 'General Volunteer',
    description: 'If you are a student, home-maker, or an elderly person with some spare time, you can assist us in many ways: spend time with children, play with them, conduct story-telling sessions, help with grocery purchases.'
  }
];

const HIGHLIGHTS = [
  {
    icon: 'fa-university',
    title: 'First in India',
    description: 'Devnar was the first institution in India to introduce computer training for the visually challenged in 2001.'
  },
  {
    icon: 'fa-cricket',
    title: 'Sports Excellence',
    description: 'The Devnar school cricket team has been regular at State, National and International levels. Players have represented India at One Day Internationals.'
  },
  {
    icon: 'fa-chess',
    title: 'Chess Champions',
    description: 'Devnar conducts FIDE Rating All India Chess Tournament every year, with players from all over India participating.'
  },
  {
    icon: 'fa-laptop',
    title: 'Assistive Technology',
    description: 'Specialized softwares such as JAWS, KURWEIL, MAGIC, PRISMA, and Low Vision Clinic ensure the best learning process.'
  }
];

// --- Reusable Components ---

function Reveal({ as: Tag = 'div', className = '', delay = 0, children, ...rest }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref]);
  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? 'reveal-visible' : ''} ${className}`}
      style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

// --- Main Volunteer Component ---

export default function Volunteer() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    city: '',
    role: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState({ submitted: false, success: false, message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userId, setUserId] = useState(null);
  const [volunteers, setVolunteers] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loadingVolunteers, setLoadingVolunteers] = useState(true);
  const [loadingTestimonials, setLoadingTestimonials] = useState(true);

  // Check for logged in user
  useEffect(() => {
    const checkUser = () => {
      try {
        const user = localStorage.getItem('user');
        if (user) {
          const userData = JSON.parse(user);
          const id = userData.id || userData.user_id || null;
          if (id) {
            setUserId(id);
          }
        }
      } catch (e) {
        console.error('Error checking user:', e);
      }
    };
    checkUser();
    fetchVolunteers();
    fetchVolunteerTestimonials();
  }, []);

  // Fetch active volunteers from database
  const fetchVolunteers = async () => {
    try {
      setLoadingVolunteers(true);
      const result = await apiService.fetchVolunteers();
      console.log('📋 Volunteers response:', result);
      
      if (result.status === 'success' && result.data) {
        // Filter only active volunteers
        const activeVolunteers = result.data.filter(v => v.status === 'active');
        setVolunteers(activeVolunteers);
      }
    } catch (error) {
      console.error('Error fetching volunteers:', error);
    } finally {
      setLoadingVolunteers(false);
    }
  };

  // Fetch volunteer testimonials from database
  const fetchVolunteerTestimonials = async () => {
    try {
      setLoadingTestimonials(true);
      const result = await apiService.fetchVolunteerTestimonials();
      console.log('📋 Volunteer Testimonials response:', result);
      
      if (result.status === 'success' && result.data) {
        // Filter only active testimonials
        const activeTestimonials = result.data.filter(t => t.status === 'active');
        setTestimonials(activeTestimonials);
      }
    } catch (error) {
      console.error('Error fetching volunteer testimonials:', error);
    } finally {
      setLoadingTestimonials(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Prepare data for volunteer_registrations table
      const volunteerData = {
        user_id: userId || null,
        full_name: formData.fullName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        city: formData.city.trim() || null,
        role: formData.role || null,
        message: formData.message.trim(),
        status: 'pending'
      };

      console.log('📤 Sending volunteer registration:', volunteerData);

      // Save to volunteer_registrations table
      const result = await apiService.saveData('volunteer_registrations', volunteerData);
      
      console.log('📝 Registration result:', result);

      if (result.success) {
        setFormStatus({
          submitted: true,
          success: true,
          message: 'Thank you for registering as a volunteer! Our team will review your application and reach out to you shortly.'
        });
        setFormData({ fullName: '', email: '', phone: '', city: '', role: '', message: '' });
      } else {
        setFormStatus({
          submitted: true,
          success: false,
          message: result.message || 'Failed to register. Please try again.'
        });
      }
    } catch (error) {
      console.error('❌ Registration error:', error);
      setFormStatus({
        submitted: true,
        success: false,
        message: 'Network error: ' + (error.message || 'Please check your connection and try again.')
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to parse image from JSON
  const getImageUrl = (imageData) => {
    if (!imageData) return null;
    try {
      const parsed = typeof imageData === 'string' ? JSON.parse(imageData) : imageData;
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed[0];
      }
      return parsed;
    } catch (e) {
      return imageData;
    }
  };

  // Clean HTML from Summernote
  const cleanHtml = (html) => {
    if (!html) return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  // Check if there are any active volunteers
  const hasVolunteers = volunteers.length > 0;

  // Check if there are any active testimonials
  const hasTestimonials = testimonials.length > 0;

  return (
    <>
      <Header />
      <div className="volunteer-page devnar">
        <a className="skip-link" href="#main-volunteer">Skip to main content</a>

        {/* --- Page Hero --- */}
        <section className="page-hero-volunteer">
          <div className="hero-background">
            <div className="hero-overlay"></div>
            <div className="hero-pattern"></div>
          </div>
          <div className="wrap hero-content-volunteer">
            <Reveal>
              <span className="hero-badge">Get Involved</span>
              <h1>Volunteer Registration</h1>
              <p className="hero-subtitle">
                Hands that give. Hearts that support. Join us in empowering visually challenged 
                children to build a brighter future.
              </p>
            </Reveal>
          </div>
        </section>

        <main id="main-volunteer" className="wrap page-content-volunteer">

          {/* --- Intro Section (Static) --- */}
          <section className="volunteer-intro">
            <Reveal className="section-title" as="div">
              <span className="eyebrow">Join Our Mission</span>
              <h2>Become a Volunteer</h2>
              <div className="rule" />
              <p className="section-subtitle">
                A hand held out in help is the greatest human asset. Be the hand that reaches out to empower others. 
                At Devnar, we treasure our volunteers as our biggest strength.
              </p>
            </Reveal>

            <div className="intro-text">
              <p>
                Be a Devnar Volunteer and help visually challenged children experience a positive vibe. 
                Be enriched in the process. There are endless ways you can choose to become a volunteer.
              </p>
            </div>
          </section>

          {/* --- Highlights Section (Static) --- */}
          <section className="volunteer-highlights">
            <Reveal className="section-title" as="div">
              <span className="eyebrow">Our Programs</span>
              <h2>Education and Beyond</h2>
              <div className="rule" />
            </Reveal>

            <div className="highlights-grid">
              {HIGHLIGHTS.map((item, index) => (
                <Reveal key={item.title} className="highlight-card" delay={index * 60}>
                  <div className="highlight-icon">
                    <i className={`fas ${item.icon}`}></i>
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </Reveal>
              ))}
            </div>
          </section>

          {/* --- Roles Section (Static) --- */}
          <section className="volunteer-roles">
            <Reveal className="section-title" as="div">
              <span className="eyebrow">Find Your Place</span>
              <h2>Ways You Can Volunteer</h2>
              <div className="rule" />
              <p className="section-subtitle">
                Above all, be our strength in any way you think would help us! Volunteers are a part of 
                our support system and we need you!
              </p>
            </Reveal>

            <div className="roles-grid">
              {VOLUNTEER_ROLES.map((role, index) => (
                <Reveal key={role.title} className="role-card" delay={(index % 9) * 40}>
                  <div className="role-icon">
                    <i className={`fas ${role.icon}`}></i>
                  </div>
                  <h3>{role.title}</h3>
                  <p>{role.description}</p>
                </Reveal>
              ))}
            </div>

            <div className="roles-cta">
              <p className="roles-closing">
                All we can assure you is that you will never be disappointed with the experience. 
                As the little hands clutch yours in gratitude and their lips curve in that perfect smile, 
                you will engrave an everlasting memory in your heart!
              </p>
            </div>
          </section>

          {/* --- Active Volunteers Section (Dynamic) --- */}
          {hasVolunteers && (
            <section className="volunteer-showcase">
              <Reveal className="section-title" as="div">
                <span className="eyebrow">Our Family</span>
                <h2>Meet Our Volunteers</h2>
                <div className="rule" />
                <p className="section-subtitle">
                  These amazing individuals are part of the Devnar family, making a difference every day.
                </p>
              </Reveal>

              {loadingVolunteers ? (
                <div className="loading-spinner-container">
                  <div className="spinner"></div>
                  <p>Loading volunteers...</p>
                </div>
              ) : (
                <div className="volunteers-grid">
                  {volunteers.map((volunteer, index) => {
                    const imageUrl = getImageUrl(volunteer.image);
                    return (
                      <Reveal key={volunteer.id} className="volunteer-card" delay={index * 60}>
                        {imageUrl ? (
                          <img 
                            src={imageUrl} 
                            alt={volunteer.full_name_en || volunteer.full_name} 
                            className="volunteer-avatar"
                            loading="lazy"
                          />
                        ) : (
                          <div className="volunteer-avatar-placeholder">
                            <i className="fas fa-user"></i>
                          </div>
                        )}
                        <h4>{volunteer.full_name_en || volunteer.full_name}</h4>
                        {volunteer.role && <span className="volunteer-role">{volunteer.role}</span>}
                        {volunteer.skills_en && (
                          <p className="volunteer-skills">
                            {cleanHtml(volunteer.skills_en)}
                          </p>
                        )}
                        {volunteer.joined_date && (
                          <span className="volunteer-joined">
                            <i className="fas fa-calendar-alt"></i> Joined {new Date(volunteer.joined_date).toLocaleDateString()}
                          </span>
                        )}
                      </Reveal>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {/* --- Testimonials Section (Dynamic from volunteer_testimonials table) --- */}
          {hasTestimonials && (
            <section className="volunteer-testimonials">
              <Reveal className="section-title" as="div">
                <span className="eyebrow">In Their Words</span>
                <h2>What Our Volunteers Say</h2>
                <div className="rule" />
              </Reveal>

              {loadingTestimonials ? (
                <div className="loading-spinner-container">
                  <div className="spinner"></div>
                  <p>Loading testimonials...</p>
                </div>
              ) : (
                <div className="testimonials-grid">
                  {testimonials.map((testimonial, index) => {
                    const imageUrl = getImageUrl(testimonial.image);
                    return (
                      <Reveal key={testimonial.id} className="testimonial-card" delay={index * 60}>
                        <div className="testimonial-header">
                          {imageUrl ? (
                            <img 
                              src={imageUrl} 
                              alt={testimonial.name} 
                              loading="lazy"
                            />
                          ) : (
                            <div className="testimonial-avatar-placeholder">
                              <i className="fas fa-user-circle"></i>
                            </div>
                          )}
                          <div className="testimonial-info">
                            <h4>{testimonial.name}</h4>
                            {testimonial.designation && (
                              <span className="testimonial-designation">
                                {cleanHtml(testimonial.designation)}
                              </span>
                            )}
                          </div>
                        </div>
                        <p className="testimonial-text">
                          "{cleanHtml(testimonial.testimonial)}"
                        </p>
                      </Reveal>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {/* --- Registration Form Section (Static) --- */}
          <section id="volunteer-form" className="volunteer-form-section">
            <Reveal className="section-title" as="div">
              <span className="eyebrow">Get Started</span>
              <h2>Volunteer Registration</h2>
              <div className="rule" />
            </Reveal>

            <div className="form-wrapper">
              {formStatus.submitted && formStatus.success ? (
                <div className="form-success">
                  <i className="fas fa-check-circle success-icon"></i>
                  <h3>Registration Successful!</h3>
                  <p>{formStatus.message}</p>
                  <p className="success-note">We look forward to welcoming you to the Devnar family!</p>
                </div>
              ) : formStatus.submitted && !formStatus.success ? (
                <div className="form-error">
                  <i className="fas fa-exclamation-circle error-icon"></i>
                  <h3>Registration Failed</h3>
                  <p>{formStatus.message}</p>
                  <button 
                    className="btn btn-secondary"
                    onClick={() => {
                      setFormStatus({ submitted: false, success: false, message: '' });
                      setIsSubmitting(false);
                    }}
                  >
                    <i className="fas fa-redo"></i> Try Again
                  </button>
                </div>
              ) : (
                <form className="volunteer-form" onSubmit={handleSubmit}>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="fullName">Full Name *</label>
                      <input
                        type="text"
                        id="fullName"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                        placeholder="Your full name"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="email">Email Address *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="phone">Phone Number *</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="Your phone number"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="city">City</label>
                      <input
                        type="text"
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        placeholder="Your city"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="role">Area of Interest</label>
                    <select
                      id="role"
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                    >
                      <option value="">Select an area of interest...</option>
                      {VOLUNTEER_ROLES.map(role => (
                        <option key={role.title} value={role.title}>{role.title}</option>
                      ))}
                      <option value="other">Other (please specify in message)</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">How would you like to volunteer? *</label>
                    <textarea
                      id="message"
                      name="message"
                      rows="4"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Tell us about your skills, availability, and how you'd like to contribute..."
                    />
                  </div>

                  <div className="form-actions">
                    <button 
                      type="submit" 
                      className="btn btn-primary submit-btn"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner"></span> Submitting...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane"></i> Register as Volunteer
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </section>

        </main>
      </div>
      <Footer />
    </>
  );
}